<template>
    <div class="min-h-screen bg-slate-50 py-12 px-4">
        <div class="mx-auto w-full max-w-4xl space-y-10">
            <div class="space-y-3 text-center">
                <h1 class="text-3xl font-semibold text-slate-900">Create Your Candidate Profile</h1>
                <p class="text-base text-slate-500">
                    Access live opportunities and detailed insights on companies, projects, and hiring activity.
                </p>
            </div>

            <StepIndicator
                :current-step="currentVisibleStep"
                :total-steps="visibleSteps.length"
                :step-labels="visibleSteps.map(step => step.name)"
            />

            <div class="rounded-3xl border border-slate-100 bg-white p-6 shadow-xl shadow-slate-200/40 md:p-10">
                <transition name="fade">
                    <div v-if="error" class="mb-6 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
                        {{ errorMessage }}
                    </div>
                </transition>

                <!-- Step Components -->
                <div class="space-y-6">
                    <StepOne v-if="currentStepKey === 'login'" :ref="currentStepRef" :formData="formData.stepOne" @submit-step1="handleStep1Data" />
                    <StepTwo v-if="currentStepKey === 'personal'" :ref="currentStepRef" :formData="formData.stepTwo" :show-industry-role="registrationFlow === 'simplified'" @submit-step2="handleStep2Data" />
                    <StepThree v-if="currentStepKey === 'career'" :ref="currentStepRef" :formData="formData.stepThree" @submit-step3="handleStep3Data" />
                    <StepFour v-if="currentStepKey === 'resume'" :ref="currentStepRef" :formData="formData.stepFour" @submit-step4="handleStep4Data" />
                    <ThankYou v-if="currentStepKey === 'thank-you'" />
                </div>

                <!-- Navigation Buttons -->
                <div v-if="currentStep < steps.length - 1" class="mt-10 flex flex-col gap-4 border-t border-slate-100 pt-6 md:flex-row md:items-center">
                    <button
                        v-if="currentStep > 0"
                        @click="previousStep"
                        class="inline-flex items-center justify-center rounded-full border border-slate-300 px-6 py-3 text-sm font-medium text-slate-600 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
                    >
                        Previous
                    </button>

                    <div class="ml-auto flex gap-3">
                        <button
                            v-if="currentStep < steps.length - 2"
                            @click="validateStepAndProceed"
                            class="inline-flex items-center justify-center rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-brand/20 transition hover:bg-brand/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
                        >
                            Next
                        </button>
                        <button
                            v-if="currentStep === steps.length - 2"
                            @click="submitForm"
                            :disabled="!canSubmit"
                            :class="[
                                'inline-flex items-center justify-center rounded-full px-6 py-3 text-sm font-semibold shadow-lg transition focus:outline-none focus-visible:ring-2',
                                canSubmit
                                    ? 'bg-emerald-500 text-white shadow-emerald-500/20 hover:bg-emerald-500/90 focus-visible:ring-emerald-400/60'
                                    : 'bg-gray-200 text-gray-500 shadow-none cursor-not-allowed focus-visible:ring-transparent'
                            ]"
                        >
                            Submit
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import StepIndicator from '@/components/marketplace/company/registration/StepIndicator.vue';
import StepOne from '@/components/marketplace/contractor/register/Step1.vue';
import StepTwo from '@/components/marketplace/contractor/register/Step2.vue';
import StepThree from '@/components/marketplace/contractor/register/Step3.vue';
import StepFour from '@/components/marketplace/contractor/register/Step4.vue';
import ThankYou from '@/components/marketplace/contractor/register/ThankYou.vue';

import contractorService from '@/services/marketplace/contractor.service';

const CONTRACTOR_REGISTRATION_FLOW = 'simplified'; // Change to 'full' to restore the old 4-form-step flow.

export default {
    components: { StepIndicator, StepOne, StepTwo, StepThree, StepFour, ThankYou },
    inject: ['toggleLoading'],
    data() {
        return {
            completedSteps: [], // Track completed steps
            registrationFlow: CONTRACTOR_REGISTRATION_FLOW,
            steps: CONTRACTOR_REGISTRATION_FLOW === 'simplified'
                ? [
                    { key: 'login', name: 'Login Details' },
                    { key: 'personal', name: 'Personal Details' },
                    { key: 'resume', name: 'Resume Upload' },
                    { key: 'thank-you', name: 'Thank You' }
                ]
                : [
                    { key: 'login', name: 'Login Details' },
                    { key: 'personal', name: 'Personal Details' },
                    { key: 'career', name: 'Career Info' },
                    { key: 'resume', name: 'Resume Upload' },
                    { key: 'thank-you', name: 'Thank You' }
                ],
            formData: {
                stepOne: {},
                stepTwo: {},
                stepThree: {},
                stepFour: {
                    profileVisibility: 'public'
                }
            },
            error: false,
            errorMessage: '',
            submitDisabled: false
        };
    },
    computed: {
        currentStep() {
            const requestedStepKey = this.$route.params.step;
            const stepIndex = this.steps.findIndex(step => step.key === requestedStepKey);
            return stepIndex >= 0 ? stepIndex : 0;
        },
        visibleSteps() {
            return this.steps.slice(0, this.steps.length - 1);
        },
        currentVisibleStep() {
            return Math.min(this.currentStep, this.visibleSteps.length - 1);
        },
        currentStepConfig() {
            return this.steps[this.currentStep] || {};
        },
        currentStepKey() {
            return this.currentStepConfig.key;
        },
        currentStepRef() {
            return `step${this.currentStep}`;
        },
        canSubmit() {
            if (this.currentStepKey !== 'resume') {
                return true;
            }
            const stepFour = this.formData.stepFour || {};
            return !!(stepFour.isFileUploaded && stepFour.uploadedFile);
        }
    },
    watch: {
        '$route.params.step': {
            immediate: true,
            handler() {
                this.ensureValidStepRoute();
            }
        }
    },
    methods: {
        getStepRoute(index) {
            const step = this.steps[index] || this.steps[0];

            return {
                name: 'ContractorRegistration',
                params: { step: step.key }
            };
        },

        routeToStep(index, replace = false) {
            const route = this.getStepRoute(index);
            const method = replace ? 'replace' : 'push';
            this.$router[method](route);
        },

        ensureValidStepRoute() {
            const requestedStepKey = this.$route.params.step;
            const stepIndex = this.steps.findIndex(step => step.key === requestedStepKey);

            if (stepIndex === -1) {
                this.routeToStep(0, true);
            }
        },

        // Check if the step is completed
        isStepCompleted(index) {
            return this.completedSteps.includes(index);
        },

        // Check if the step is accessible (either completed or the current step)
        isStepAccessible(index) {
            return index <= this.currentStep || this.isStepCompleted(index);
        },

        // Navigate to a step (if accessible)
        goToStep(index) {
            if (this.isStepAccessible(index)) {
                this.routeToStep(index);
            }
        },


        // Handle emitted data from Step 1
        handleStep1Data(stepOneData) {
            this.formData.stepOne = stepOneData;  // Store the data for Step 1
        },

        // Handle emitted data from Step 2
        handleStep2Data(stepTwoData) {
            this.formData.stepTwo = stepTwoData;  // Store the data for Step 2
        },

        // Handle emitted data from Step 3
        handleStep3Data(stepThreeData) {
            this.formData.stepThree = stepThreeData;
        },

        // Handle emitted data from Step 4
        handleStep4Data(stepFourData) {
            this.formData.stepFour = stepFourData;
        },

        // Validate the current step and proceed if valid
        async validateStepAndProceed() {
            const currentStepComponent = this.$refs[`step${this.currentStep}`];
            if (currentStepComponent && await currentStepComponent.validate()) {
                this.nextStep();
            }
        },

        // Navigate to the next step and mark the current one as completed
        nextStep() {
            if (!this.completedSteps.includes(this.currentStep)) {
                this.completedSteps.push(this.currentStep);
            }
            if (this.currentStep < this.steps.length - 1) {
                this.routeToStep(this.currentStep + 1);
            }
        },

        // Navigate to the previous step
        previousStep() {
            if (this.currentStep > 0) {
                this.routeToStep(this.currentStep - 1);
            }
        },
        async submitForm() {
            this.toggleLoading(true);
            if (this.submitDisabled) {
                this.toggleLoading(false);
                return
            }
            const currentStepComponent = this.$refs[`step${this.currentStep}`];

            if (currentStepComponent && currentStepComponent.validate()) {
                try {
                    // Combine all steps into a single object
                    const flatFormData = {
                        ...this.formData.stepOne,
                        ...this.formData.stepTwo,
                        ...this.formData.stepThree, // This contains arrays
                        // ...this.formData.stepFour,   // Contains uploaded file data
                    };

                    if (flatFormData.croppedImage) {
                        delete flatFormData.croppedImage;
                    }

                    // Serialize questions and answers
                    flatFormData.questions = JSON.stringify(flatFormData.questions || [])
                    flatFormData.answers = JSON.stringify(flatFormData.answers || [])

                    // Create a FormData object to handle file upload and other data
                    const formData = new FormData();
                    for (let key in flatFormData) {
                        formData.append(key, flatFormData[key]);
                    }

                    // Append the uploaded file from Step 4
                    if (this.formData.stepFour.uploadedFile) {
                        formData.append('resume', this.formData.stepFour.uploadedFile);  // 'resume' is the file field
                    }

                    formData.append('profileVisibility', this.formData.stepFour.profileVisibility || 'public');

                    // Append the uploaded profile picture file
                    if (this.formData.stepTwo.profilePicture) {
                        formData.append('profilePicture', this.formData.stepTwo.profilePicture);
                    }

                    contractorService.register(formData)
                        .then(response => {
                            if (response.data && response.data.registrationError) {
                                this.error =  true;
                                this.errorMessage = 'There was an issue while submitting your information. Please try filling this form again in a few minutes'
                                this.submitDisabled = true
                            } else {
                                if (!this.completedSteps.includes(this.currentStep)) {
                                    this.completedSteps.push(this.currentStep);
                                }
                                this.routeToStep(this.steps.length - 1);
                            }
                            this.toggleLoading(false);
                        })
                        .catch(error => {
                            // Handle error if the API call fails
                            this.error =  true;
                            this.errorMessage = 'There was an issue while submitting your information. Please try filling this form again in a few minutes'
                            this.submitDisabled = true
                            this.toggleLoading(false);
                        });
                } catch (error) {
                    this.error =  true;
                    this.errorMessage = 'There was an issue while submitting your information. Please try filling this form again in a few minutes'
                    this.submitDisabled = true
                    this.toggleLoading(false);
                }
            } else {
                this.toggleLoading(false);
            }
        }
    }
};
</script>

<style scoped>
button {
    transition: all 0.3s ease;
}
</style>
